import * as Sentry from '@sentry/vue';
import axios from 'axios';
import bowser from 'bowser';
import DownloadsDialog from 'components/DownloadsDialog/DownloadsDialog';
import exports from 'components/Exports/Exports';
import reports from 'components/Reports/Reports';
import Url from 'domurl';
import Hammer from 'hammerjs';
import { trackDashboardEvents } from 'js/eventsTracking';
import httpClient from 'js/httpClient';
import assetSelectorMixin from 'js/mixins/assetSelectorMixin';
import { registerLegacyVuetifyCompat } from 'js/mixins/legacyVuetifyCompat';
import viewsInfoMixin from 'js/mixins/viewsInfoMixin';
import { canShowUploadToolbarButton } from 'js/mixins/toolbarButtonVisibility';
import vuetify, { vuetifyIconAliases } from 'js/vuetify';
import { applyLegacyPlugins } from 'js/vueCompat';
import {
    buildDatasetGroupFromPackage,
    buildDatasetGroupSelectionState,
    buildDatasetDatetimeOptions,
    buildViewerMenuItems,
    buildShellMenuState,
    buildSiteSelectionResetState,
    hasAvailableShellDates,
    hasSelectableShellViews,
    resolveDatasetDatetimeOption,
    resolveShellViewType,
    shouldAutoSelectSingleDatasetGroup,
} from 'js/viewers/shellSelectorHelpers';
import { findExistingViewerPane } from 'js/viewers/viewNavigation';
import { hydrateDatasetGroupVersions } from 'js/viewers/datasetGroupSelection';
import {
    concat,
    filter,
    find,
    findIndex,
    findKey,
    keys,
    matches,
    matchesProperty,
    pickBy,
    union,
    values,
    debounce,
} from 'lodash';
import pinia from 'store/pinia';
import VueKonva from 'vue-konva';
import { mapState, mapActions } from 'pinia';
import { useSmartviewStore } from 'store/pinia/useSmartviewStore';
import {
    applyDarkThemeToDocument,
    useGeneralStore,
} from 'store/pinia/useGeneralStore';

const _ = {
    findIndex,
    find,
    keys,
    pickBy,
    concat,
    values,
    union,
    filter,
    matches,
    matchesProperty,
    findKey,
    debounce,
};

const browser = bowser.getParser(window.navigator.userAgent);

/**
 *@returns {boolean} true if location is dev false if not
 */
function checkLocation() {
    const locals = ['127.0.0.1', 'localhost', '192.168.'];

    // eslint-disable-next-line no-restricted-syntax
    for (const local of locals) {
        // eslint-disable-next-line no-restricted-globals
        if (location.host.includes(local)) return true;
    }
    return false;
}

let developmentServer = checkLocation();

function installIntercom() {
    (() => {
        const w = window;
        const ic = w.Intercom;
        if (typeof ic === 'function') {
            ic('reattach_activator');
            ic('update', w.intercomSettings);
        } else {
            const d = document;
            const index = (x) => {
                index.c(x);
            };
            index.q = [];
            index.c = (x) => {
                index.q.push(x);
            };
            w.Intercom = index;
            const l = () => {
                const s = d.createElement('script');
                s.type = 'text/javascript';
                s.async = true;
                s.src = 'https://widget.intercom.io/widget/aftiso2m';
                const x = d.querySelectorAll('script')[0];
                x.parentNode.insertBefore(s, x);
            };
            if (w.attachEvent) {
                w.attachEvent('onload', l);
            } else {
                w.addEventListener('load', l, false);
            }
        }
    })();
}

function installSentry(app) {
    if (checkLocation()) {
        return;
    }
    Sentry.init({
        app,
        dsn: window.VUE_SENTRY_DSN,
        release: window.RELEASE_VERSION,
        environment: window.ENVIRONMENT,
    });
    Sentry.setUser({ email: window.USER });
}

function createVuetifyCompat() {
    const theme = {};
    Object.defineProperty(theme, 'dark', {
        enumerable: true,
        get: () => Boolean(vuetify.theme?.global?.current?.value?.dark),
        set: (value) => {
            if (vuetify.theme?.global?.name?.value !== undefined) {
                vuetify.theme.global.name.value = value ? 'dark' : 'light';
            }
        },
    });

    return {
        theme,
        icons: vuetifyIconAliases,
    };
}

function normalizeVuetifyGlobal(app) {
    const globalProps = app.config.globalProperties;
    const existing =
        globalProps.$vuetify && typeof globalProps.$vuetify === 'object'
            ? globalProps.$vuetify
            : null;
    const compat = createVuetifyCompat();
    const target = existing || {};

    if (!target.theme || typeof target.theme !== 'object') {
        target.theme = compat.theme;
    } else if (!Object.prototype.hasOwnProperty.call(target.theme, 'dark')) {
        Object.defineProperty(target.theme, 'dark', {
            configurable: true,
            enumerable: true,
            get: () => compat.theme.dark,
            set: (value) => {
                compat.theme.dark = value;
            },
        });
    }
    if (!target.icons) {
        target.icons = compat.icons;
    }

    if (!existing) {
        globalProps.$vuetify = target;
    }
}

function applyLineClampDirective(app) {
    app.directive('line-clamp', {
        mounted(el, binding) {
            const lineHeight = Number(binding.arg || 20);
            const lines = Number(binding.value || 1);
            el.style.overflow = 'hidden';
            el.style.display = '-webkit-box';
            el.style.webkitBoxOrient = 'vertical';
            el.style.webkitLineClamp = String(lines);
            if (!Number.isNaN(lineHeight) && lineHeight > 0) {
                el.style.lineHeight = `${lineHeight}px`;
                el.style.maxHeight = `${lineHeight * lines}px`;
            }
        },
        updated(el, binding) {
            const lineHeight = Number(binding.arg || 20);
            const lines = Number(binding.value || 1);
            el.style.webkitLineClamp = String(lines);
            if (!Number.isNaN(lineHeight) && lineHeight > 0) {
                el.style.lineHeight = `${lineHeight}px`;
                el.style.maxHeight = `${lineHeight * lines}px`;
            }
        },
    });
}

function installVueKonvaCompat(app) {
    try {
        if (!VueKonva) {
            return;
        }
        const components = app._context?.components || {};
        // Vuetify also registers VLabel, so use a vue-konva specific component
        // to detect whether the plugin is already installed.
        if (components.VStage || components['v-stage']) {
            app.config.globalProperties.__ndsdVueKonvaInstalled = true;
            return;
        }
        if (app.config.globalProperties.__ndsdVueKonvaInstalled) {
            return;
        }
        const installed = app._context?.plugins;
        if (installed && installed.has(VueKonva)) {
            app.config.globalProperties.__ndsdVueKonvaInstalled = true;
            return;
        }
        const originalComponent = app.component.bind(app);
        const registerComponentWithoutVLabelCollision = (name, definition) => {
            const normalizedName =
                typeof name === 'string'
                    ? name.toLowerCase().replace(/[^a-z0-9]/g, '')
                    : '';
            const hasVLabel =
                app._context?.components?.VLabel ||
                app._context?.components?.['v-label'];

            if (hasVLabel && normalizedName === 'vlabel') {
                return app;
            }
            return originalComponent(name, definition);
        };

        app.component = registerComponentWithoutVLabelCollision;
        if (typeof VueKonva.install === 'function') {
            try {
                app.use(VueKonva);
                app.config.globalProperties.__ndsdVueKonvaInstalled = true;
                return;
            } finally {
                app.component = originalComponent;
            }
        }
        if (typeof VueKonva === 'function') {
            try {
                VueKonva(app);
                app.config.globalProperties.__ndsdVueKonvaInstalled = true;
            } finally {
                app.component = originalComponent;
            }
            return;
        }
        app.component = originalComponent;
    } catch (error) {
        const message =
            error &&
            (error.message || (typeof error === 'string' ? error : ''));
        if (
            typeof message === 'string' &&
            message.includes('already been registered')
        ) {
            app.config.globalProperties.__ndsdVueKonvaInstalled = true;
            return;
        }
        // eslint-disable-next-line no-console
        console.warn('Skipping vue-konva plugin install:', error);
    }
}

export function registerApp(
    app,
    { router = null, extraPlugins = [], enableVueKonva = true } = {}
) {
    const usePlugin = (plugin, ...options) => {
        if (!plugin) {
            return;
        }
        const installed = app._context?.plugins;
        if (installed && installed.has(plugin)) {
            return;
        }
        app.use(plugin, ...options);
    };

    app.config.warnHandler = (msg, vm, trace) => {
        if (
            !msg.includes('The .native modifier for v-on') &&
            !msg.includes('Duplicate keys detected') &&
            !msg.includes('sidebar') &&
            !msg.includes('Failed to mount component: template') &&
            !msg.includes('Component "VLabel" has already been registered') &&
            !msg.includes(
                'Slot "append-inner" invoked outside of the render function'
            ) &&
            !msg.includes(
                'Slot "default" invoked outside of the render function'
            ) &&
            !msg.includes(
                'Slot "extension" invoked outside of the render function'
            )
        ) {
            // eslint-disable-next-line no-console
            console.error(msg, trace, vm);
        }
    };
    app.config.devtools = true;
    app.config.compilerOptions.delimiters = ['[[', ']]'];
    app.config.globalProperties.$set = (target, key, value) => {
        target[key] = value;
        return value;
    };
    app.config.globalProperties.$delete = (target, key) => {
        delete target[key];
    };

    if (typeof module !== 'undefined' && module.hot) {
        module.hot.accept();
    }

    usePlugin(pinia);
    usePlugin(vuetify);
    applyLegacyPlugins(app);
    registerLegacyVuetifyCompat(app);
    applyLineClampDirective(app);
    if (enableVueKonva) {
        installVueKonvaCompat(app);
    }

    extraPlugins.forEach((pluginEntry) => {
        if (Array.isArray(pluginEntry)) {
            usePlugin(pluginEntry[0], ...(pluginEntry[1] || []));
            return;
        }
        usePlugin(pluginEntry);
    });

    if (router) {
        usePlugin(router);
    }

    normalizeVuetifyGlobal(app);

    developmentServer = checkLocation();
    installSentry(app);
    if (!developmentServer) {
        installIntercom();
    }
}

const mainMixin = {
    el: '#MainApp',
    delimiters: ['[[', ']]'],
    mixins: [viewsInfoMixin, assetSelectorMixin],
    components: {
        reports,
        exports,
        DownloadsDialog,
    },
    data() {
        return {
            noDatasetVersionsModal: false,
            supportedBrowserDialog: false,
            platformString: 'none',
            platformType: '',
            drawer: false,
            timeout: null,

            // sites
            sites: [],
            sites_loading: false,
            sites_search: null,
            sites_select: null,
            sites_dict: {},
            initial_site_load_status: false,
            sitesSearchTimeout: null,
            skipNextSiteSearchReset: false,

            // datasets (EXISTING PIPELINE — KEEP)
            dataset_groups_data: [],
            dataset_groups_loading: false,
            dataset_groups_search: null,
            dataset_groups_select: null,
            dataset_groups_dict: {},
            datasets_list: [],
            dataset_time_selected: null,
            datetime_select: null,
            synced_views: false,

            // views
            view_key: 0,
            views_select: null,
            views_select_loading: false,
            shellViewerList: [],
            viewerListRequestId: 0,

            // UI dialogs
            reportsDialog: false,
            showExportDialog: false,
            showDownloadsDialog: false,
            goToNewView: true,
            goToNewViewSuppressions: 0,
            hydratingViewSelection: false,
            onViewPage: false,
            intercomMessengerOpen: false,

            // NEW dataset picker UI
            siteMenu: false,
            datasetSelectMenu: false,
            datasetMenu: false,
            dateMenu: false,
            viewMenu: false,
            datasetFilterDate: [],
            datasetDisplayValue: null,
            siteDatasetSearch: null,
            _debouncedSearchDatasets: null,
            datepicker_menu: false,
            datepicker_date: null,
            datasetDateRange: {
                start: null,
                end: null,
            },
            datasetGroupsByDate: [],

            // results
            datasets: [],
            datasets_loading: false,
        };
    },
    methods: {
        ...mapActions(useSmartviewStore, [
            'reverseViewsOrder',
            'setSyncViews',
            'setSyncedViews',
            'exportCSV',
            'setViewIsFavourite',
            'setViewDatasetPackage',
        ]),
        ...mapActions(useGeneralStore, {
            setDarkThemeState: 'setDarkTheme',
        }),
        setDarkTheme(value) {
            const normalizedValue =
                typeof value === 'boolean' ? value : !this.darkTheme;
            this.setDarkThemeState(normalizedValue);
            this.applyThemeToDocument(normalizedValue);
        },
        applyThemeToDocument(isDark) {
            this.$vuetify.theme.dark = isDark;
            localStorage.darkTheme = String(isDark);
            document.documentElement.classList.toggle('theme--dark', isDark);
            document.documentElement.classList.toggle('theme--light', !isDark);
            document.body.classList.toggle('theme--dark', isDark);
            document.body.classList.toggle('theme--light', !isDark);
        },
        toggleIntercomMessenger() {
            this.intercomMessengerOpen = !this.intercomMessengerOpen;
        },
        showNewIntercomMessage(message) {
            if (!developmentServer) window.Intercom('showNewMessage', message);
        },
        clearDatasetDate() {
            this.datasetFilterDate = [];
            this.datasetDateRange = { start: null, end: null };
            this.datasetGroupsByDate = [];
            this.getDatasetGroups();
        },
        clearDatasetFilters() {
            this.dataset_groups_search = '';
            this.clearDatasetDate();
        },
        closeShellMenus(except = null) {
            Object.assign(
                this,
                buildShellMenuState(except, {
                    siteMenu: this.siteMenu,
                    datasetSelectMenu: this.datasetSelectMenu,
                    datasetMenu: this.datasetMenu,
                    dateMenu: this.dateMenu,
                    viewMenu: this.viewMenu,
                })
            );
        },
        focusShellTextField(refName) {
            this.$nextTick(() => {
                const input = this.$refs[refName]?.$el?.querySelector('input');
                input?.focus?.();
            });
        },
        resetSiteSearch() {
            if (this.skipNextSiteSearchReset) {
                this.skipNextSiteSearchReset = false;
                this.sites_search = null;
                return;
            }

            if (!this.sites_search) {
                return;
            }

            this.sites_search = null;
            this.getSites();
        },
        preserveSelectedSiteInResults(results) {
            if (
                !this.sites_select ||
                this.sites_select === 'all_sites' ||
                results.some((site) => site.uuid === this.sites_select)
            ) {
                return results;
            }

            const selectedSite =
                this.sites.find((site) => site.uuid === this.sites_select) ||
                this.sites_dict?.[this.sites_select];

            return selectedSite ? [selectedSite, ...results] : results;
        },
        handleSiteSelected(site) {
            if (!site?.uuid) {
                return;
            }

            if (this.sites_search) {
                this.skipNextSiteSearchReset = true;
                this.sites_search = null;
            }

            this.siteMenu = false;
            if (this.sites_select === site.uuid) {
                return;
            }

            this.sites_select = site.uuid;
        },
        selectDatasetGroup(group) {
            if (!group?.uuid) {
                return;
            }

            this.applyDatasetGroupSelection(group);
            this.$emit('dataset-group-selected', group);
        },
        onDatasetDatePicked(range) {
            const pickedRange = range ?? this.datasetFilterDate;
            let start = null;
            let end = null;

            if (Array.isArray(pickedRange)) {
                const normalizedDates = [
                    ...new Set(
                        pickedRange
                            .filter(Boolean)
                            .map((value) =>
                                typeof value === 'string'
                                    ? value
                                    : new Date(value).toISOString().slice(0, 10)
                            )
                    ),
                ].sort();

                if (
                    normalizedDates.length !== this.datasetFilterDate.length ||
                    normalizedDates.some(
                        (value, index) =>
                            value !== this.datasetFilterDate[index]
                    )
                ) {
                    this.datasetFilterDate = normalizedDates;
                }

                if (normalizedDates.length >= 2) {
                    start = normalizedDates[0];
                    end = normalizedDates[normalizedDates.length - 1];
                }
            } else if (pickedRange?.start && pickedRange?.end) {
                start = pickedRange.start;
                end = pickedRange.end;
            }

            if (!start || !end) {
                this.datasetDateRange = { start: null, end: null };
                this.datasetGroupsByDate = [];
                return;
            }

            const normalizedRange = {
                start: start <= end ? start : end,
                end: start <= end ? end : start,
            };

            if (
                this.datasetDateRange?.start === normalizedRange.start &&
                this.datasetDateRange?.end === normalizedRange.end
            ) {
                return;
            }

            this.datasetDateRange = normalizedRange;

            this.getDatasetGroups({
                datasetDateRange: this.datasetDateRange,
            }).then(() => {
                // ✅ show date results under picker
                this.datasetGroupsByDate = [...this.dataset_groups_data];
            });
        },

        // fetchDatasetGroups () {
        //     this.dataset_groups_loading = true

        //     axios.get('/api/dataset-groups/', {
        //         params: {
        //         search: this.dataset_groups_search,
        //         date: this.datasetFilterDate
        //         }
        //     }).then(res => {
        //         this.dataset_groups_data = res.data.results
        //     }).finally(() => {
        //         this.dataset_groups_loading = false
        //     })
        // },
        // selectDataset (ds) {
        //     // same value v-autocomplete used
        //     this.dataset_groups_select = ds.uuid

        //     // display text in activator
        //     this.datasetDisplayValue = ds.name

        //     // close menu
        //     this.datasetMenu = false

        //     // OPTIONAL: emit / call existing handlers
        //     this.onDatasetGroupChanged?.(ds.uuid)
        // },
        // async fetchDatasets(datasetGroupUuid) {
        //     try {
        //         const response = await httpClient.get(`/api/v1/datasets/?group_uuid=${datasetGroupUuid}`);
        //         // Use enrichDatasets to format if needed
        //         this.datasets = this.enrichDatasets(response.data.results);
        //     } catch (error) {
        //         console.error('Failed to fetch datasets', error);
        //         this.datasets = [];
        //     }
        // },
        enrichDatasets(datasets) {
            return datasets.map((ds) => ({
                ...ds,
                formatted_date: this.formatDate(ds.uploaded_at),
            }));
        },
        formatDate(date) {
            if (!date) return '';
            return new Date(date).toLocaleDateString('en-GB', {
                year: 'numeric',
                month: 'short',
                day: '2-digit',
            });
        },
        showOnPage(allowedPages) {
            const currentPage = window.location.pathname;
            for (const page in allowedPages) {
                if (Object.prototype.hasOwnProperty.call(allowedPages, page)) {
                    if (
                        allowedPages[page][0] === '!' &&
                        currentPage.includes(allowedPages[page].slice(1))
                    ) {
                        return false;
                    }
                    if (
                        allowedPages[page][0] !== '!' &&
                        !currentPage.includes(allowedPages[page])
                    ) {
                        return false;
                    }
                }
            }
            return true;
        },
        handleToolbarButtonClick(item) {
            if (!item || !item.enabled) {
                return;
            }

            if (typeof item.action === 'function') {
                item.action();
                return;
            }

            if (item.link) {
                window.location.assign(item.link);
            }
        },
        nth(n) {
            return (
                ['st', 'nd', 'rd'][((((n + 90) % 100) - 10) % 10) - 1] || 'th'
            );
        },
        closeViewsSelect() {
            try {
                this.viewMenu = false;
            } catch (error) {}
        },
        applyDatasetGroupSelection(
            datasetGroupOrUuid,
            { showNoDatasetVersionsModal = true } = {}
        ) {
            const datasetGroup =
                typeof datasetGroupOrUuid === 'string'
                    ? this.dataset_groups.find(
                          (group) => group.uuid === datasetGroupOrUuid
                      )
                    : datasetGroupOrUuid;

            if (!datasetGroup?.uuid) {
                return false;
            }

            Object.assign(
                this,
                buildDatasetGroupSelectionState({
                    datasetGroup,
                    isAllLocationsSelected: this.isAllLocationsSelected,
                    enrichDatasets: (datasets) => this.enrichDatasets(datasets),
                })
            );

            if (
                showNoDatasetVersionsModal &&
                !hasAvailableShellDates(this.dataset_datetimes)
            ) {
                this.noDatasetVersionsModal = true;
            }

            return true;
        },
        findDatasetGroupForPackage(datasetPackage) {
            const groupUuid = datasetPackage?.dataset_group_uuid;
            if (!groupUuid) {
                return null;
            }

            return (
                this.dataset_groups.find((group) => group.uuid === groupUuid) ||
                this.dataset_groups_data.find(
                    (group) => group.uuid === groupUuid
                ) ||
                null
            );
        },
        upsertDatasetPackageShellOptions(datasetPackage) {
            const datasetGroup = buildDatasetGroupFromPackage(
                datasetPackage,
                this.findDatasetGroupForPackage(datasetPackage) || {}
            );

            if (!datasetGroup?.uuid) {
                return null;
            }

            const siteUuid = datasetGroup.site_uuid || datasetPackage.site_uuid;
            if (siteUuid) {
                const existingSite =
                    this.sites_dict?.[siteUuid] ||
                    this.sites.find((site) => site.uuid === siteUuid) ||
                    {};

                this.upsertMapShellSite(
                    {
                        ...existingSite,
                        uuid: siteUuid,
                        name:
                            existingSite.name ||
                            datasetPackage.site_name ||
                            datasetGroup.site_name ||
                            'Selected Location',
                    },
                    datasetGroup
                );
            }

            this.dataset_groups_data = [
                datasetGroup,
                ...this.dataset_groups_data.filter(
                    (group) => group.uuid !== datasetGroup.uuid
                ),
            ];

            return datasetGroup;
        },
        async hydrateDatasetGroupVersions(datasetGroup) {
            return hydrateDatasetGroupVersions(datasetGroup);
        },
        suppressGoToNewView() {
            // Reentrancy-safe suppression: overlapping async callers (e.g.
            // several panes emitting loaded-dataset-package at once) must not
            // re-enable goToNewView while another caller is still mid-flight,
            // or the views_select watcher runs changeViewMain/replaceViews
            // and collapses the page to a single view.
            this.goToNewViewSuppressions += 1;
            this.goToNewView = false;
        },
        releaseGoToNewView() {
            this.goToNewViewSuppressions = Math.max(
                0,
                this.goToNewViewSuppressions - 1
            );
            if (this.goToNewViewSuppressions === 0) {
                this.goToNewView = true;
            }
        },
        async handleDatasetPackage({
            datasetPackage,
            viewUUID,
            siteSelectorValue,
        } = {}) {
            // console.log(typeof datasetPackage.site_uuid);
            // console.log(datasetPackage);
            if (!datasetPackage?.dataset_group_uuid) {
                return;
            }

            const selectedSiteValue =
                datasetPackage.site_uuid ||
                (siteSelectorValue && siteSelectorValue !== 'all_sites'
                    ? siteSelectorValue
                    : null);

            if (
                selectedSiteValue &&
                !this.sites_dict?.[selectedSiteValue] &&
                typeof this.getSites === 'function'
            ) {
                await this.getSites({ uuid: selectedSiteValue });
            }

            const datasetGroup =
                this.upsertDatasetPackageShellOptions(datasetPackage);
            if (!datasetGroup) {
                return;
            }

            const nextViewUuid =
                viewUUID || this.selectedViewUuid || this.views_select || null;
            const previousDatetimeSelect = this.datetime_select;
            this.suppressGoToNewView();
            this.hydratingViewSelection = true;

            try {
                if (
                    selectedSiteValue &&
                    this.sites_select !== selectedSiteValue
                ) {
                    this.sites_select = selectedSiteValue;
                    await this.$nextTick();
                }

                const hydratedDatasetGroup =
                    await this.hydrateDatasetGroupVersions(datasetGroup);

                this.dataset_groups_data = [
                    hydratedDatasetGroup,
                    ...this.dataset_groups_data.filter(
                        (group) => group.uuid !== hydratedDatasetGroup.uuid
                    ),
                ];

                this.applyDatasetGroupSelection(hydratedDatasetGroup, {
                    showNoDatasetVersionsModal: false,
                });
                await this.$nextTick();

                const datasetPackageDatetime = resolveDatasetDatetimeOption(
                    this.dataset_datetimes,
                    datasetPackage
                );
                const retainedDatetime = resolveDatasetDatetimeOption(
                    this.dataset_datetimes,
                    previousDatetimeSelect
                );

                this.datetime_select =
                    datasetPackageDatetime || retainedDatetime || null;
                await this.$nextTick();

                this.views_select = nextViewUuid;
                await this.$nextTick();
                this.closeViewsSelect();
            } finally {
                this.hydratingViewSelection = false;
                this.releaseGoToNewView();
            }
        },
        unloadPage() {
            return 'You have unsaved changes on this page. Are you sure you want to leave?';
        },
        handleDatasetGroupSelected(datasetGroup) {
            this.applyDatasetGroupSelection(datasetGroup);
        },
        normaliseMapDatasetMap(datasets) {
            if (!datasets) {
                return {};
            }

            const datasetList = Array.isArray(datasets)
                ? datasets
                : Object.values(datasets);

            return datasetList.reduce((map, dataset) => {
                if (dataset?.uuid) {
                    map[dataset.uuid] = dataset;
                }
                return map;
            }, {});
        },
        upsertMapShellSite(site, datasetGroup) {
            const siteUuid = site?.uuid || datasetGroup?.site_uuid;

            if (!siteUuid) {
                return null;
            }

            const existingSite =
                this.sites_dict?.[siteUuid] ||
                this.sites.find((item) => item.uuid === siteUuid) ||
                {};
            const existingGroups = Array.isArray(existingSite.dataset_groups)
                ? existingSite.dataset_groups
                : Object.values(existingSite.dataset_groups || {});
            const groupsMap = existingGroups.reduce((map, group) => {
                if (group?.uuid) {
                    map[group.uuid] = group;
                }
                return map;
            }, {});

            groupsMap[datasetGroup.uuid] = {
                ...groupsMap[datasetGroup.uuid],
                ...datasetGroup,
            };

            const selectedSite = {
                ...existingSite,
                ...site,
                uuid: siteUuid,
                name:
                    site?.name ||
                    existingSite.name ||
                    datasetGroup.site_name ||
                    datasetGroup.name,
                dataset_groups: groupsMap,
            };
            const allLocations = this.sites.find(
                (item) => item.uuid === 'all_sites'
            ) || {
                name: 'All Locations',
                uuid: 'all_sites',
            };
            const otherSites = this.sites.filter(
                (item) => item.uuid !== 'all_sites' && item.uuid !== siteUuid
            );

            this.sites_dict = {
                ...this.sites_dict,
                [siteUuid]: selectedSite,
            };
            this.sites = [allLocations, selectedSite, ...otherSites];

            return selectedSite;
        },
        findMapDatasetForView(datasetsMap, selectedView = null) {
            const datasets = Object.values(datasetsMap || {});

            if (!datasets.length) {
                return null;
            }

            if (selectedView?.viewType && selectedView?.viewUuid) {
                const matchingDataset = datasets.find(
                    (dataset) =>
                        dataset[selectedView.viewType] === selectedView.viewUuid
                );

                if (matchingDataset) {
                    return matchingDataset;
                }
            }

            return datasets
                .filter((dataset) =>
                    Object.keys(this.view_options).some(
                        (viewType) =>
                            dataset[viewType] &&
                            dataset[`${viewType}_published`] !== false
                    )
                )
                .sort((first, second) => {
                    const firstDate = new Date(
                        `${first.date || ''} ${first.time || ''}`.trim()
                    );
                    const secondDate = new Date(
                        `${second.date || ''} ${second.time || ''}`.trim()
                    );
                    return secondDate - firstDate;
                })[0];
        },
        findMapDatetimeOption(dataset) {
            if (!dataset?.date) {
                return null;
            }

            return (
                this.dataset_datetimes.find(
                    (option) =>
                        option.date === dataset.date &&
                        option.time === dataset.time
                ) ||
                this.dataset_datetimes.find(
                    (option) => option.date === dataset.date
                ) ||
                null
            );
        },
        findDatasetForDatetimeSelection(datetimeOption = null) {
            if (!datetimeOption) {
                return null;
            }

            const datasetList = Array.isArray(this.datasets_list)
                ? this.datasets_list
                : Object.values(this.datasets_list || {});

            if (!datasetList.length) {
                return null;
            }

            return (
                datasetList.find(
                    (dataset) =>
                        dataset?.date === datetimeOption.date &&
                        dataset?.time === datetimeOption.time
                ) ||
                datasetList.find(
                    (dataset) => dataset?.date === datetimeOption.date
                ) ||
                null
            );
        },
        async refreshViewerListForSelectedDate(datetimeOption = null) {
            const requestId = ++this.viewerListRequestId;
            const siteUuid = this.sites_select;
            const datasetGroupUuid = this.dataset_groups_select;
            const dataset = this.findDatasetForDatetimeSelection(datetimeOption);
            if (!dataset?.uuid) {
                return null;
            }

            const response = await axios.get(
                `/api/dataset/package/${dataset.uuid}`
            );
            const datasetPackage = response?.data || null;
            const currentDataset = this.findDatasetForDatetimeSelection(
                this.datetime_select
            );
            if (
                !datasetPackage ||
                requestId !== this.viewerListRequestId ||
                siteUuid !== this.sites_select ||
                datasetGroupUuid !== this.dataset_groups_select ||
                currentDataset?.uuid !== dataset.uuid
            ) {
                return null;
            }

            // A date selection changes menu options. A view selection changes panes.
            this.shellViewerList = datasetPackage.viewerList || [];

            return datasetPackage;
        },
        findMapViewOption(selectedView = null) {
            const selectedViewType = resolveShellViewType(
                selectedView?.viewType
            );
            if (selectedView?.viewUuid) {
                const matchingView = this.views.find(
                    (view) => view.uuid === selectedView.viewUuid
                );

                if (matchingView) {
                    return matchingView;
                }
            }

            if (selectedViewType) {
                const matchingView = this.views.find(
                    (view) => view.type === selectedViewType
                );

                if (matchingView) {
                    return matchingView;
                }
            }

            return this.views[0] || null;
        },
        hasSelectedViewForCurrentDataset() {
            const selectedViewUuid = this.selectedViewUuid;
            if (!selectedViewUuid) {
                return false;
            }

            return this.views.some(
                (view) => String(view?.uuid || '') === String(selectedViewUuid)
            );
        },
        async handleMapAssetSelected({
            site,
            datasetGroup,
            datasets = null,
            selectedDataset = null,
            selectedView = null,
        } = {}) {
            if (!datasetGroup?.uuid) {
                return;
            }

            const datasetsMap = this.normaliseMapDatasetMap(
                datasets || datasetGroup.datasets
            );
            const enrichedDatasetGroup = {
                ...datasetGroup,
                site_uuid: datasetGroup.site_uuid || site?.uuid || null,
                site_name: datasetGroup.site_name || site?.name || null,
                datasets: datasetsMap,
            };
            const selectedSite = this.upsertMapShellSite(
                site,
                enrichedDatasetGroup
            );

            const previousDatetimeSelect = this.datetime_select;
            this.suppressGoToNewView();

            try {
                if (
                    selectedSite?.uuid &&
                    this.sites_select !== selectedSite.uuid
                ) {
                    this.sites_select = selectedSite.uuid;
                    await this.$nextTick();
                }

                const hydratedDatasetGroup =
                    await this.hydrateDatasetGroupVersions(
                        enrichedDatasetGroup
                    );

                this.dataset_groups_data = [
                    hydratedDatasetGroup,
                    ...this.dataset_groups_data.filter(
                        (group) => group.uuid !== hydratedDatasetGroup.uuid
                    ),
                ];

                this.applyDatasetGroupSelection(hydratedDatasetGroup, {
                    showNoDatasetVersionsModal: false,
                });
                await this.$nextTick();

                const datasetForSelection =
                    selectedDataset ||
                    this.findMapDatasetForView(
                        hydratedDatasetGroup.datasets,
                        selectedView
                    );
                const datasetSelectionDatetime = datasetForSelection
                    ? this.findMapDatetimeOption(datasetForSelection)
                    : null;
                const retainedDatetime = resolveDatasetDatetimeOption(
                    this.dataset_datetimes,
                    previousDatetimeSelect
                );
                const datetimeOption =
                    datasetSelectionDatetime || retainedDatetime;

                this.datetime_select =
                    datetimeOption || this.dataset_datetimes[0] || null;
                await this.$nextTick();

                const viewOption = this.findMapViewOption(selectedView);

                if (viewOption?.uuid) {
                    this.views_select = viewOption.uuid;
                }

                this.closeShellMenus();
            } finally {
                this.$nextTick(() => {
                    this.releaseGoToNewView();
                });
            }
        },
        handleDateSelected(datetimeOption) {
            if (!datetimeOption) {
                return;
            }

            this.datetime_select = datetimeOption;
            this.dateMenu = false;
        },
        handleViewSelected(view) {
            const viewUuid = view?.uuid;

            if (!viewUuid || view.notPublished) {
                return;
            }

            // Treat an explicit menu click as a user navigation request, even
            // if the shell is still hydrating selection state in the
            // background. The watcher still handles programmatic updates, but
            // direct clicks should always open the chosen view.
            this.goToNewView = false;
            this.views_select = viewUuid;
            this.viewMenu = false;
            this.changeViewMain(view);
            this.$nextTick(() => {
                this.goToNewView = true;
            });
        },
        changeViewMain(view) {
            const viewUuid = view?.uuid;
            if (!viewUuid) {
                return;
            }

            if (this.onViewPage) {
                const existingPane = findExistingViewerPane(
                    this.viewsOnPage,
                    viewUuid
                );
                const viewOption =
                    view?.type && view?.uuid
                        ? view
                        : this.views.find(
                              option => option.uuid === viewUuid
                          ) || view;

                this.replaceViews(
                    existingPane || this.createViewForSelection(viewOption)
                );
                return;
            }

            // console.log('selected site :', this.sites_select);
            if (this.sites_select == 'all_sites') {
                let dataset_group = this.dataset_groups.find(
                    (dataset_group) =>
                        dataset_group.uuid === this.dataset_groups_select
                );
                let site_uuid = dataset_group.site_uuid;
                window.location.href = `/view?view=${viewUuid}&site=${site_uuid}`;
            } else {
                window.location.href = `/view?view=${viewUuid}&site=${this.sites_select}`;
            }
        },
        getSites({
            uuid = null,
            name = null,
            pageSize = null,
            initial_load = false,
        } = {}) {
            this.sites_loading = true;
            const endpoint =
                name === null && uuid === null
                    ? pageSize === null
                        ? '/api/v1/sites/'
                        : `/api/v1/sites/?size=${pageSize}`
                    : uuid === null
                      ? `/api/v1/sites/?name=${name}`
                      : `/api/v1/sites/?uuid=${uuid}`;
            return httpClient
                .get(endpoint)
                .then((response) => {
                    // var totalCount = response.data['count'];
                    // var next = response.data['next'];
                    // var previous = response.data['previous'];
                    var results = this.preserveSelectedSiteInResults(
                        response.data['results'] || []
                    );
                    this.sites = [
                        { name: 'All Locations', uuid: 'all_sites' },
                        ...results,
                    ]; // or [{'ss'}].concat(results);
                    // console.log(this.sites);
                    this.sites_loading = false;
                    this.dataset_groups_loading = false;
                    this.sites_dict = this.converSitetArrToDic(results);
                    // set initial_site_load_status true if it's still false
                    if (initial_load) {
                        if (!this.initial_site_load_status) {
                            this.initial_site_load_status = true;
                        }
                    }
                })
                .catch((error) => {
                    this.sites_loading = false;
                    this.dataset_groups_loading = false;
                    // Unauthenticated pages (e.g. login/register) can receive 401.
                    if (error?.response?.status !== 401) {
                        // eslint-disable-next-line no-console
                        console.error(error);
                    }
                });
        },
        async getDatasetGroups({
            uuid = null,
            name = null,
            pageSize = null,
            datasetDateRange = null,
        } = {}) {
            this.dataset_groups_loading = true;
            const params = {};
            if (pageSize) params.size = pageSize;
            if (uuid) params.uuid = uuid;
            if (name) params.name = name;
            if (datasetDateRange?.start && datasetDateRange?.end) {
                params.dataset_date_after = datasetDateRange.start;
                params.dataset_date_before = datasetDateRange.end;
            }

            try {
                const response = await httpClient.get(
                    '/api/v1/datasetgroups/',
                    { params }
                );
                this.dataset_groups_data = [...response.data.results];
                // console.log('API response:', this.dataset_groups_data);
            } catch (err) {
                if (err?.response?.status !== 401) {
                    // eslint-disable-next-line no-console
                    console.error(err);
                }
            } finally {
                this.dataset_groups_loading = false;
            }
        },
        selectDataset(ds) {
            this.applyDatasetGroupSelection(ds);
        },
        validWordID(wordID) {
            return wordID.match(/^(?:[A-Za-z]+-){2}[A-Za-z]+$/) !== null;
        },
        toggleSelectedViewFavourite() {
            if (this.isFavourite) {
                this.removeFromFavourites(this.selectedViewDetails);
            } else {
                this.addToFavourites(this.selectedViewDetails);
            }
        },
        addToFavourites(view) {
            httpClient
                .post('/api/add_favourite_view/', {
                    viewUuid: view.viewUUID,
                })
                .then((response) => {
                    if (response.data.success) {
                        this.setViewIsFavourite([this.selectedView, true]);
                    }
                })
                .catch((error) => {
                    throw error;
                });
            trackDashboardEvents.favouriteAdded(
                this.viewsOnPage[this.selectedView]
            );
        },
        removeFromFavourites(view) {
            httpClient
                .post('/api/remove_favourite_view/', {
                    viewUuid: view.viewUUID,
                })
                .then((response) => {
                    if (response.data.success) {
                        this.setViewIsFavourite([this.selectedView, false]);
                    }
                })
                .catch((error) => {
                    throw error;
                });
            trackDashboardEvents.favouriteRemoved(
                this.viewsOnPage[this.selectedView]
            );
        },
        converSitetArrToDic(site_array) {
            /**
             * This method convert site json array from API and convert it to Dictionary
             * used by the app for quick data lookup
             * @author: Williams Francis <w.francis@nationaldrones.com> | <olawalewilliams9438@gmail.com>
             */
            var data_dict = {};

            site_array.forEach((site) => {
                var sites_dic = { [site.uuid]: site };

                var dataset_groups = sites_dic[site.uuid].dataset_groups;
                var dataset_group_list = Array.isArray(dataset_groups)
                    ? dataset_groups
                    : Object.values(dataset_groups || {});

                // Test dataset_group is null or undefined return else proceed
                if (
                    dataset_group_list === null ||
                    dataset_group_list === 'undefined'
                )
                    return;

                var dataset_group_dics = {};
                dataset_group_list.forEach((dataset_group) => {
                    var dataset_group_dic = {
                        [dataset_group.uuid]: dataset_group,
                    };
                    Object.assign(dataset_group_dics, dataset_group_dic);

                    sites_dic[site.uuid].dataset_groups = dataset_group_dics;

                    var datasets = dataset_group.datasets;
                    var dataset_list = Array.isArray(datasets)
                        ? datasets
                        : Object.values(datasets || {});

                    // Test dataset_group is null or undefined return else proceed
                    if (
                        dataset_list === null ||
                        dataset_list === 'undefined'
                    )
                        return;

                    var dataset_dics = {};
                    dataset_list.forEach((dataset) => {
                        var dataset_dic = { [dataset.uuid]: dataset };
                        Object.assign(dataset_dics, dataset_dic);

                        sites_dic[site.uuid].dataset_groups[
                            dataset_group.uuid
                        ].datasets = dataset_dics;
                    });
                });

                Object.assign(data_dict, sites_dic);
            });
            // console.log(data_dict);

            return data_dict;
        },
    },
    created() {
        /**
         * This is where the mainMixing initial data needed for the page is loaded
         * and the site or all_sites get parameter is also gotten from if available to be used on the page.
         * @author: Williams Francis <w.francis@nationaldrones.com> | <olawalewilliams9438@gmail.com>
         *
         */
        const authPagePaths = [
            '/accounts/login',
            '/accounts/register',
            '/accounts/password/reset/request',
            '/accounts/password/reset/confirm',
        ];
        const pathname = window.location.pathname || '';
        if (
            window.SD_SKIP_SHELL_BOOTSTRAP === true ||
            authPagePaths.some((path) => pathname.startsWith(path))
        ) {
            return;
        }

        const queryString = window.location.search;
        const urlParameters = new URLSearchParams(queryString);
        if (urlParameters.has('site')) {
            const site_uuid = urlParameters.get('site');
            // console.log(site_uuid);
            this.getSites({ uuid: site_uuid, initial_load: true });
        } else {
            this.getSites();
            this.getDatasetGroups();
        }

        // debounce wrapper that calls getDatasetGroups
        this._debouncedSearchDatasets = debounce(
            (q) => this.getDatasetGroups({ name: q }),
            400
        );
    },
    computed: {
        ...mapState(useGeneralStore, {
            darkTheme: 'darkTheme',
        }),
        ...mapState(useSmartviewStore, {
            getIdCounter: 'getIdCounter',
            viewsOnPage: 'viewsOnPage',
            selectedViewValue: 'selectedViewValue',
            selectedView: 'selectedViewValue',
            syncViewsValue: 'syncViewsValue',
            selectedViewDetails: 'selectedViewDetails',
            selectedViewIsFavourite: 'selectedViewIsFavourite',
            isReportModeValue: 'isReportModeValue',
            cameraDataValue: 'cameraDataValue',
            debuggingBackToSourceValue: 'debuggingBackToSourceValue',
        }),
        isAllLocationsSelected() {
            return (
                this.sites_select === 'all_sites' || this.sites_select === null
            );
        },
        hasDateRange() {
            return (
                (Array.isArray(this.datasetFilterDate) &&
                    this.datasetFilterDate.length >= 2 &&
                    this.datasetFilterDate[0] &&
                    this.datasetFilterDate[
                        this.datasetFilterDate.length - 1
                    ]) ||
                (this.datasetDateRange?.start && this.datasetDateRange?.end)
            );
        },
        filteredDatasetGroups() {
            if (!this.dataset_groups_search) return this.dataset_groups_data;
            const q = this.dataset_groups_search.toLowerCase();
            return this.dataset_groups_data.filter((dg) =>
                dg.name.toLowerCase().includes(q)
            );
        },
        filteredSiteDatasetGroups() {
            if (!this.siteDatasetSearch) {
                return this.dataset_groups;
            }

            const query = this.siteDatasetSearch.toLowerCase();
            return this.dataset_groups.filter((group) =>
                group.name.toLowerCase().includes(query)
            );
        },
        isFavourite() {
            return this.selectedViewIsFavourite;
        },
        siteShellLabel() {
            if (this.sites_select === 'all_sites') {
                return 'All Locations';
            }

            if (!this.sites_select) {
                return 'Select Location';
            }

            const selectedSite = this.sites.find(
                (site) => site.uuid === this.sites_select
            );

            return (
                selectedSite?.name ||
                this.sites_dict?.[this.sites_select]?.name ||
                'Select Location'
            );
        },
        datasetShellLabel() {
            if (this.isAllLocationsSelected) {
                return this.datasetDisplayValue || 'Select Asset';
            }

            const selectedGroup = this.dataset_groups.find(
                (group) => group.uuid === this.dataset_groups_select
            );

            return (
                selectedGroup?.name ||
                this.datasetDisplayValue ||
                'Select Asset'
            );
        },
        dateShellLabel() {
            return this.datetime_select?.display || 'Select Date';
        },
        selectedViewUuid() {
            return typeof this.views_select === 'object'
                ? this.views_select?.uuid || null
                : this.views_select;
        },
        viewShellLabel() {
            if (!this.selectedViewUuid) {
                return 'Select View';
            }

            if (typeof this.views_select === 'object') {
                return (
                    this.views_select.text ||
                    this.views.find(
                        (view) => view.uuid === this.views_select.uuid
                    )?.text ||
                    'Select View'
                );
            }

            return (
                this.views.find((view) => view.uuid === this.selectedViewUuid)
                    ?.text || 'Select View'
            );
        },
        canOpenDateSelector() {
            return hasAvailableShellDates(this.dataset_datetimes);
        },
        canOpenViewSelector() {
            return hasSelectableShellViews(this.views);
        },
        reportsEnabled() {
            return this.viewsOnPage && this.viewsOnPage[this.selectedViewValue]
                ? this.viewsOnPage[this.selectedViewValue].enableReports
                : false;
        },
        viewCount() {
            return Array.isArray(this.viewsOnPage)
                ? this.viewsOnPage.length
                : Number(this.viewsOnPage?.length || 0);
        },
        hasMultipleViews() {
            return this.viewCount > 1;
        },
        toolbar_buttons() {
            // a list of the buttons in the right side of the app bar, will automatically move into a menu when the window shrinks
            return [
                {
                    icon: '$vuetify.icons.upload',
                    text: 'Upload Asset',
                    align: 'left',
                    enabled: true,
                    action: false,
                    link: '/upload',
                    type: 'text',
                    showOnPage:
                        canShowUploadToolbarButton({
                            pageVisible: this.showOnPage([
                                '!/view',
                                '!/upload',
                                '!/accounts/plan/select',
                            ]),
                            canUpload: window.USER_CAN_UPLOAD,
                            errorPage: window.errorPage,
                        }),
                },
                {
                    icon: this.synced_views
                        ? '$vuetify.icons.unSync'
                        : '$vuetify.icons.sync',
                    text: this.synced_views
                        ? 'Stop Syncing Views'
                        : 'Sync Views',
                    align: 'left',
                    enabled: this.hasMultipleViews,
                    action: () => {
                        if (!this.synced_views) {
                            trackDashboardEvents.syncViewUsed(this.viewsOnPage);
                        }
                        this.synced_views = !this.synced_views;
                    },
                    link: null,
                    type: 'icon',
                    showOnPage: this.showOnPage(['/view']),
                },
                {
                    icon: '$vuetify.icons.close',
                    text: `Close ${
                        this.selectedViewValue +
                        1 +
                        this.nth(this.selectedViewValue + 1)
                    } View`,
                    align: 'left',
                    enabled: this.hasMultipleViews,
                    action: () => {
                        this.removeView(this.selectedViewValue);
                    },
                    link: null,
                    type: 'icon',
                    showOnPage: this.showOnPage(['/view']),
                },
                {
                    icon: '$vuetify.icons.expandView',
                    text: `Expand ${
                        this.selectedViewValue +
                        1 +
                        this.nth(this.selectedViewValue + 1)
                    } View`,
                    align: 'left',
                    enabled: this.hasMultipleViews,
                    action: () => {
                        this.replaceViews(this.selectedViewDetails);
                    },
                    link: null,
                    type: 'icon',
                    showOnPage: this.showOnPage(['/view']),
                },
                {
                    icon: '$vuetify.icons.swapViews',
                    text: `Swap Views`,
                    align: 'left',
                    enabled: this.hasMultipleViews,
                    action: () => {
                        this.reverseViewsOrder();
                    },
                    link: null,
                    type: 'icon',
                    showOnPage: this.showOnPage(['/view']),
                },
                {
                    icon: '$vuetify.icons.report',
                    text: `Reports for ${
                        this.selectedViewValue +
                        1 +
                        this.nth(this.selectedViewValue + 1)
                    } View`,
                    align: 'left',
                    enabled: this.reportsEnabled,
                    action: () => {
                        this.reportsDialog = true;
                    },
                    link: null,
                    type: 'icon',
                    showOnPage: this.showOnPage(['/view']),
                },
                {
                    icon: 'mdi-view-dashboard-outline',
                    text: 'Asset Dashboard',
                    align: 'left',
                    enabled: this.assetDashboardButtonEnabled,
                    action: null,
                    link: this.assetDashboardLink,
                    type: 'icon',
                    showOnPage: this.showOnPage(['/view']),
                },
                {
                    icon: this.isFavourite
                        ? '$vuetify.icons.solidStar'
                        : '$vuetify.icons.hollowStar',
                    text: this.isFavourite
                        ? 'Remove from favourites.'
                        : 'Add to favourites',
                    align: 'left',
                    enabled: true,
                    action: () => {
                        this.toggleSelectedViewFavourite();
                    },
                    link: null,
                    type: 'icon',
                    showOnPage: this.showOnPage(['/view']),
                },
                {
                    icon: '$vuetify.icons.fileExportReg',
                    text: 'Export files',
                    align: 'left',
                    enabled: this.reportsEnabled,
                    action: () => {
                        this.showExportDialog = true;
                    },
                    type: 'icon',
                    showOnPage: this.showOnPage(['/view']),
                },
                {
                    icon: '$vuetify.icons.outlineDownload',
                    text: 'Download Asset',
                    align: 'left',
                    enabled: true,
                    action: () => {
                        this.showDownloadsDialog = true;
                        console.log(
                            'selectedViewDetails: ',
                            this.selectedViewDetails
                        );
                        console.log('viewsOnPage: ', this.viewsOnPage);
                    },
                    type: 'icon',
                    showOnPage: this.showOnPage(['/view']),
                },
                {
                    icon: '$vuetify.icons.settingsOutline',
                    text: 'Manage Asset',
                    align: 'left',
                    enabled: true,
                    action: () => {
                        const siteUuid =
                            this.selectedViewDetails.datasetPackage.site_uuid;
                        const datasetGroupUuid =
                            this.selectedViewDetails.datasetPackage
                                .dataset_group_uuid;
                        window.location = `/locations/${siteUuid}/${datasetGroupUuid}`;
                    },
                    type: 'icon',
                    showOnPage:
                        this.showOnPage(['/view']) &&
                        this.manageAssetButtonEnabled,
                },
            ];
        },
        numberOfToolbarButtons() {
            return _.keys(
                _.pickBy(this.toolbar_buttons, (o) => {
                    return o.showOnPage;
                })
            ).length;
        },
        // sites menu computed
        // eslint-disable-next-line sonarjs/cognitive-complexity
        dataset_groups() {
            let array = [];
            if (this.sites_select === 'all_sites') {
                // if 'all sites' is selected get all of the datasets from datasetgroup API (/api/v1/datasetgroups/)
                // eslint-disable-next-line no-restricted-syntax
                array = this.dataset_groups_data;
            } else if (this.sites.length > 0 && this.sites_select !== null) {
                try {
                    // if there are sites and a site is selected get its datasets
                    const dict =
                        this.sites_dict[this.sites_select].dataset_groups;
                    array = _.values(dict);
                } catch (e) {
                    // This is a temp fix for veiws link without site param
                    const queryString = window.location.search;
                    const urlParameters = new URLSearchParams(queryString);
                    if (!urlParameters.has('site')) {
                        //if no site param use dummy
                        const dict =
                            this.sites_dict[this.sites[3].uuid].dataset_groups;
                        array = _.values(dict);
                    } else {
                        array = [];
                    }
                }
            }

            const viewTypes = Object.keys(this.view_options);

            array.forEach((dsGroup) => {
                if (dsGroup.datasets) {
                    return;
                }

                dsGroup.datasets.forEach((ds) => {
                    // eslint-disable-next-line no-restricted-syntax
                    for (const viewType in viewTypes) {
                        if (ds[`${viewType}_published`]) {
                            break;
                        }
                    }
                });
            });

            return array;
        },
        views() {
            const viewerList =
                this.shellViewerList.length > 0
                    ? this.shellViewerList
                    : this.selectedViewDetails?.datasetPackage?.viewerList ||
                      [];
            return buildViewerMenuItems(viewerList, this.view_options);
        },
        // eslint-disable-next-line sonarjs/cognitive-complexity
        dataset_datetimes() {
            const availableDatasets = Array.isArray(this.datasets_list)
                ? this.datasets_list.filter((dataset) => {
                      for (const viewType in this.view_options) {
                          if (dataset[`${viewType}_published`]) {
                              return true;
                          }
                      }
                      return false;
                  })
                : Object.values(this.datasets_list || {}).filter((dataset) => {
                      for (const viewType in this.view_options) {
                          if (dataset[`${viewType}_published`]) {
                              return true;
                          }
                      }
                      return false;
                  });

            return buildDatasetDatetimeOptions(availableDatasets);
        },
        showSiteSelect() {
            return (
                !window.errorPage &&
                // eslint-disable-next-line no-restricted-globals
                location.pathname !== '/accounts/plan/select'
            );
        },
        xs() {
            return Boolean(vuetify.display?.xs?.value ?? vuetify.display?.xs);
        },
        sm() {
            return Boolean(vuetify.display?.sm?.value ?? vuetify.display?.sm);
        },
        smAndUp() {
            return Boolean(
                vuetify.display?.smAndUp?.value ?? vuetify.display?.smAndUp
            );
        },
        mdAndUp() {
            return Boolean(
                vuetify.display?.mdAndUp?.value ?? vuetify.display?.mdAndUp
            );
        },
        mdAndDown() {
            return Boolean(
                vuetify.display?.mdAndDown?.value ?? vuetify.display?.mdAndDown
            );
        },

        // base template computed
        logoUrl() {
            return '/static/img/logos/Smartdata_square_L.png';
        },
        bigLogoUrl() {
            if (this.darkTheme) {
                return '/static/img/logos/Smartdata_light_L.png';
            }
            return '/static/img/logos/Smartdata_dark_L.png';
        },
        isOSDarkMode() {
            return window.matchMedia('(prefers-color-scheme: dark)').matches;
        },
        supportedBrowser() {
            return browser.satisfies({
                chrome: '>65',
                firefox: '>60',
                safari: '>10',
                edge: '>10',
            });
        },
        isTouchScreen() {
            return 'ontouchstart' in document.documentElement;
        },
        assetDashboardLink() {
            const datasetUuid =
                this.selectedViewDetails?.datasetPackage?.uuid || null;
            return datasetUuid ? `/asset/${datasetUuid}/` : null;
        },
        assetDashboardButtonEnabled() {
            return Boolean(this.assetDashboardLink);
        },
        manageAssetButtonEnabled() {
            if (
                !this.selectedViewDetails ||
                !this.selectedViewDetails.datasetPackage
            ) {
                return false;
            }
            return this.selectedViewDetails.datasetPackage.userCanEditSite;
        },

        // protected variables
        siteDataDict() {
            // console.log(userSitesDict);
            // eslint-disable-next-line no-undef
            return typeof userSitesDict !== 'undefined' ? userSitesDict : {}; // pulling from window variable set in base.html template
        },
        // eslint-disable-next-line unicorn/prevent-abbreviations
        siteDataArr() {
            // console.log(userSitesArray)
            // eslint-disable-next-line no-undef
            return typeof userSitesArray !== 'undefined' ? userSitesArray : []; // pulling from window variable set in base.html template
        },
        googleMapsKey() {
            return window.GOOGLE_MAPS_API_KEY || '';
        },
    },
    watch: {
        initial_site_load_status() {
            if (this.initial_site_load_status) {
                this.handleDatasetPackage(this.viewsOnPage[this.selectedView]);
            }
        },
        intercomMessengerOpen(value) {
            if (!developmentServer) {
                if (value) {
                    window.Intercom('show');
                } else {
                    window.Intercom('hide');
                }
            }
        },
        dataset_groups_search(q) {
            if (!q) return;
            if (typeof this._debouncedSearchDatasets !== 'function') return;

            // Clear date filter when searching
            this.datasetFilterDate = [];
            this.datasetDateRange = { start: null, end: null };
            this.datasetGroupsByDate = [];

            // ✅ pass the parameter
            this._debouncedSearchDatasets(q);
        },
        reportsDialog(value) {
            const url = new Url();
            if (value) {
                url.query.reports = true;
            } else {
                delete url.query.reports;
            }
            // eslint-disable-next-line no-restricted-globals
            history.pushState(null, null, url);
        },
        synced_views(value) {
            const url = new Url();
            this.setSyncedViews(this.synced_views);

            if (value) {
                url.query.sync = true;
            } else {
                delete url.query.sync;
            }
            // eslint-disable-next-line no-restricted-globals
            history.pushState(null, null, url);
        },
        views_select(value) {
            this.viewMenu = false;
            const viewUuid = typeof value === 'object' ? value?.uuid : value;

            if (!viewUuid) {
                return;
            }

            if (this.goToNewView) {
                this.changeViewMain({ uuid: viewUuid });
            }
        },
        siteMenu(value) {
            if (value) {
                this.closeShellMenus('siteMenu');
                this.focusShellTextField('site_search_input');
            } else {
                this.resetSiteSearch();
            }
        },
        datasetSelectMenu(value) {
            if (value) {
                this.closeShellMenus('datasetSelectMenu');
                this.focusShellTextField('dataset_group_search_input');
            } else {
                this.siteDatasetSearch = null;
            }
        },
        datasetMenu(value) {
            if (value) {
                this.closeShellMenus('datasetMenu');
            }
        },
        dateMenu(value) {
            if (!value) {
                return;
            }

            if (!this.canOpenDateSelector) {
                this.dateMenu = false;
                return;
            }

            this.closeShellMenus('dateMenu');
        },
        viewMenu(value) {
            if (!value) {
                return;
            }

            if (!this.canOpenViewSelector) {
                this.viewMenu = false;
                return;
            }

            this.closeShellMenus('viewMenu');
        },
        // sites menu watch
        sites_select(value, oldValue) {
            if (!value || value === oldValue) {
                return;
            }

            const preservedViewSelection = this.hydratingViewSelection
                ? this.views_select
                : null;

            Object.assign(this, buildSiteSelectionResetState());

            if (preservedViewSelection) {
                this.views_select = preservedViewSelection;
            }

            this.$nextTick(() => {
                if (shouldAutoSelectSingleDatasetGroup(this.dataset_groups)) {
                    this.applyDatasetGroupSelection(this.dataset_groups[0]);
                }
            });
        },
        // eslint-disable-next-line no-unused-vars
        sites_search(value) {
            // Clear previous timeout
            if (this.sitesSearchTimeout) {
                clearTimeout(this.sitesSearchTimeout);
            }

            // Guard empty input
            if (!value || value.length < 2) {
                return;
            }

            // Wait until user stops typing
            this.sitesSearchTimeout = setTimeout(() => {
                if (this.sites_loading) return;
                this.getSites({ name: value });
            }, 1000);
        },
        // dataset_groups_select(value) {
        //     let data;

        //     // eslint-disable-next-line no-restricted-syntax
        //     for (const dataset in this.dataset_groups) {
        //         if (
        //             Object.prototype.hasOwnProperty.call(
        //                 this.dataset_groups,
        //                 dataset
        //             ) &&
        //             this.dataset_groups[dataset].uuid ===
        //                 this.dataset_groups_select
        //         ) {
        //             data = this.dataset_groups[dataset].datasets;
        //         }
        //     }

        //     this.datasets_list = data;
        //     this.datetime_select = null; // reset the date

        //     this.$nextTick(() => {
        //         try {
        //             if (value) {
        //                 this.$refs.dataset_autocomplete.isFocused = false;
        //                 if (this.dataset_datetimes.length === 0) {
        //                     this.noDatasetVersionsModal = true;
        //                 } else {
        //                     // focus the autocomplete and drop down the menu
        //                     this.$refs.date_autocomplete.isFocused = true;
        //                     this.$nextTick(() => {
        //                         this.$refs.date_autocomplete.isMenuActive = true;
        //                     });
        //                 }
        //             }
        //         } catch (error) {
        //             // console.log("isMenuActive and isFocused not loaded/available yet: ", err.message);
        //         }
        //     });
        // },
        async datetime_select(value) {
            const siteUuid = this.sites_select;
            const datasetGroupUuid = this.dataset_groups_select;
            let requestId = this.viewerListRequestId;
            const isCurrentSelection = () =>
                this.datetime_select === value &&
                this.sites_select === siteUuid &&
                this.dataset_groups_select === datasetGroupUuid &&
                this.viewerListRequestId === requestId;
            try {
                if (!value) return;

                this.dateMenu = false;
                this.views_select_loading = true;
                const request = this.refreshViewerListForSelectedDate(value);
                requestId = this.viewerListRequestId;
                const datasetPackage = await request;
                if (!datasetPackage || !isCurrentSelection()) {
                    return;
                }
                this.viewMenu = !this.hasSelectedViewForCurrentDataset();
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error('Failed to refresh viewer list for date', error);
            } finally {
                if (isCurrentSelection()) {
                    this.views_select_loading = false;
                }
            }
        },
        // eslint-disable-next-line no-unused-vars
        // dataset_groups_search(value) {
        //     let input = document.getElementById('dataset_groups_search');

        //     // Listen for keystroke events
        //     input.addEventListener('keyup', e => {
        //         // Clear the timeout if it has already been set.
        //         // This will prevent the previous task from executing
        //         clearTimeout(this.timeout);

        //         // Make a new timeout set to go off in 1000ms (1 second)
        //         this.timeout = setTimeout(() => {
        //             // console.log('Input Value:', input.value);

        //             // Items have already been requested
        //             if (this.dataset_groups_loading) return;

        //             this.getDatasetGroups({ name: input.value });
        //         }, 1000);
        //     });
        // },
        // other watch
        darkTheme() {
            this.applyThemeToDocument(this.darkTheme);

            const style = document.createElement('style');
            const styleDiv = document.querySelector('#styleDiv');

            if (this.darkTheme) {
                styleDiv.append(style);
                style.append(
                    document.createTextNode(
                        'input:-webkit-autofill,\n' +
                            'input:-webkit-autofill:hover,\n' +
                            'input:-webkit-autofill:focus,\n' +
                            'textarea:-webkit-autofill,\n' +
                            'textarea:-webkit-autofill:hover,\n' +
                            'textarea:-webkit-autofill:focus,\n' +
                            'select:-webkit-autofill,\n' +
                            'select:-webkit-autofill:hover,\n' +
                            'select:-webkit-autofill:focus {\n' +
                            '    -webkit-text-fill-color: #fff!important;\n' +
                            '}'
                    )
                );
                style.append(
                    document.createTextNode(
                        'div.v-dialog__container > div > div > div > div.v-card__actions > button.v-btn.v-btn--contained.theme--light.v-size--default.primary > span, .v-card.v-sheet.v-sheet--tile, .v-card__text, .v-btn.v-btn--contained.theme--light.v-size--default, .leaflet-popup-content-wrapper, .leaflet-popup-tip, .leaflet-control-zoom-in, .leaflet-control-zoom-out, #toggle-mode-btn, #dataset-view-button, .leaflet-bar a:not(.color-button) {\n' +
                            '    background-color: #1E1E1E!important;\n' +
                            '    color: #FFFFFF!important;\n' +
                            '}'
                    )
                );
                style.append(
                    document.createTextNode(
                        '.leaflet-touch .leaflet-control-layers, .leaflet-touch .leaflet-bar {\n' +
                            '    border-color: #666666 !important;\n' +
                            '}'
                    )
                );
                style.append(
                    document.createTextNode(
                        '.leaflet-draw-toolbar a {\n' +
                            "    background-image: url(\"data:image/svg+xml,%3Csvg xmlns:dc='http://purl.org/dc/elements/1.1/' xmlns:cc='http://creativecommons.org/ns%23' xmlns:rdf='http://www.w3.org/1999/02/22-rdf-syntax-ns%23' xmlns:svg='http://www.w3.org/2000/svg' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink' xmlns:sodipodi='http://sodipodi.sourceforge.net/DTD/sodipodi-0.dtd' xmlns:inkscape='http://www.inkscape.org/namespaces/inkscape' height='60' width='600' version='1.1' inkscape:version='0.91 r13725' sodipodi:docname='spritesheet.svg' inkscape:export-filename='/home/fpuga/development/upstream/icarto.Leaflet.draw/src/images/spritesheet-2x.png' inkscape:export-xdpi='90' inkscape:export-ydpi='90' style=''%3E%3Crect id='backgroundrect' width='100%25' height='100%25' x='0' y='0' fill='none' stroke='none'/%3E%3Cmetadata id='metadata4258'%3E%3Crdf:RDF%3E%3Ccc:Work rdf:about=''%3E%3Cdc:format%3Eimage/svg+xml%3C/dc:format%3E%3Cdc:type rdf:resource='http://purl.org/dc/dcmitype/StillImage'/%3E%3Cdc:title/%3E%3C/cc:Work%3E%3C/rdf:RDF%3E%3C/metadata%3E%3Cdefs id='defs4256'/%3E%3Csodipodi:namedview pagecolor='%23ffffff' bordercolor='%23666666' borderopacity='1' objecttolerance='10' gridtolerance='10' guidetolerance='10' inkscape:pageopacity='0' inkscape:pageshadow='2' inkscape:window-width='1920' inkscape:window-height='1056' id='namedview4254' showgrid='false' inkscape:zoom='1.3101852' inkscape:cx='237.56928' inkscape:cy='7.2419621' inkscape:window-x='1920' inkscape:window-y='24' inkscape:window-maximized='1' inkscape:current-layer='svg4225'/%3E%3Cg class='currentLayer' style=''%3E%3Ctitle%3ELayer 1%3C/title%3E%3Cg id='enabled' style='' class='' fill='%23ffffff' fill-opacity='1'%3E%3Cg id='polyline' style='' fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='m 18,36 0,6 6,0 0,-6 -6,0 z m 4,4 -2,0 0,-2 2,0 0,2 z' id='path4229' inkscape:connector-curvature='0' style='' fill='%23ffffff' fill-opacity='1'/%3E%3Cpath d='m 36,18 0,6 6,0 0,-6 -6,0 z m 4,4 -2,0 0,-2 2,0 0,2 z' id='path4231' inkscape:connector-curvature='0' style='' fill='%23ffffff' fill-opacity='1'/%3E%3Cpath d='m 23.142,39.145 -2.285,-2.29 16,-15.998 2.285,2.285 z' id='path4233' inkscape:connector-curvature='0' style='' fill='%23ffffff' fill-opacity='1'/%3E%3C/g%3E%3Cpath id='polygon' d='M 100,24.565 97.904,39.395 83.07,42 76,28.773 86.463,18 Z' inkscape:connector-curvature='0' style='' fill='%23ffffff' fill-opacity='1'/%3E%3Cpath id='rectangle' d='m 140,20 20,0 0,20 -20,0 z' inkscape:connector-curvature='0' style='' fill='%23ffffff' fill-opacity='1'/%3E%3Cpath id='circle' d='m 221,30 c 0,6.078 -4.926,11 -11,11 -6.074,0 -11,-4.922 -11,-11 0,-6.074 4.926,-11 11,-11 6.074,0 11,4.926 11,11 z' inkscape:connector-curvature='0' style='' fill='%23ffffff' fill-opacity='1'/%3E%3Cpath id='marker' d='m 270,19 c -4.971,0 -9,4.029 -9,9 0,4.971 5.001,12 9,14 4.001,-2 9,-9.029 9,-14 0,-4.971 -4.029,-9 -9,-9 z m 0,12.5 c -2.484,0 -4.5,-2.014 -4.5,-4.5 0,-2.484 2.016,-4.5 4.5,-4.5 2.485,0 4.5,2.016 4.5,4.5 0,2.486 -2.015,4.5 -4.5,4.5 z' inkscape:connector-curvature='0' style='' fill='%23ffffff' fill-opacity='1'/%3E%3Cg id='edit' style='' fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='m 337,30.156 0,0.407 0,5.604 c 0,1.658 -1.344,3 -3,3 l -10,0 c -1.655,0 -3,-1.342 -3,-3 l 0,-10 c 0,-1.657 1.345,-3 3,-3 l 6.345,0 3.19,-3.17 -9.535,0 c -3.313,0 -6,2.687 -6,6 l 0,10 c 0,3.313 2.687,6 6,6 l 10,0 c 3.314,0 6,-2.687 6,-6 l 0,-8.809 -3,2.968' id='path4240' inkscape:connector-curvature='0' style='' fill='%23ffffff' fill-opacity='1'/%3E%3Cpath d='m 338.72,24.637 -8.892,8.892 -2.828,0 0,-2.829 8.89,-8.89 z' id='path4242' inkscape:connector-curvature='0' style='' fill='%23ffffff' fill-opacity='1'/%3E%3Cpath d='m340.7130818841731,22.643304864723405 l-2.8279335498809814,0 l0,-2.8279335498809814 l2.8279335498809814,0 z' id='path4244' inkscape:connector-curvature='0' style='' fill='%23ffffff' fill-opacity='1'/%3E%3C/g%3E%3Cg id='remove' style='' fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='m 381,42 18,0 0,-18 -18,0 0,18 z m 14,-16 2,0 0,14 -2,0 0,-14 z m -4,0 2,0 0,14 -2,0 0,-14 z m -4,0 2,0 0,14 -2,0 0,-14 z m -4,0 2,0 0,14 -2,0 0,-14 z' id='path4247' inkscape:connector-curvature='0' style='' fill='%23ffffff' fill-opacity='1'/%3E%3Cpath d='m 395,20 0,-4 -10,0 0,4 -6,0 0,2 22,0 0,-2 -6,0 z m -2,0 -6,0 0,-2 6,0 0,2 z' id='path4249' inkscape:connector-curvature='0' style='' fill='%23ffffff' fill-opacity='1'/%3E%3C/g%3E%3C/g%3E%3Cg id='disabled' style='fill:%23bbbbbb'%3E%3Cuse xlink:href='%23edit' id='edit-disabled' x='120' y='0' width='100%25' height='100%25'/%3E%3Cuse xlink:href='%23remove' id='remove-disabled' x='120' y='0' width='100%25' height='100%25'/%3E%3C/g%3E%3Cpath style='stroke-width: 2px; stroke-miterlimit: 4; stroke-dasharray: none;' id='circle-3' d='m 581.65725,30 c 0,6.078 -4.926,11 -11,11 -6.074,0 -11,-4.922 -11,-11 0,-6.074 4.926,-11 11,-11 6.074,0 11,4.926 11,11 z' inkscape:connector-curvature='0' class='selected' fill='none' stroke='%23ffffff' stroke-opacity='1'/%3E%3C/g%3E%3C/svg%3E\") !important;\n" +
                            '}'
                    )
                );
                style.append(
                    document.createTextNode(
                        '.leaflet-container:not(.leaflet-control-minimap) {\n' +
                            'background: #303030 !important;\n' +
                            '}'
                    )
                );
                style.append(
                    document.createTextNode(
                        '.handle-bl {\n' +
                            'background-color: #1E1E1E;\n' +
                            "background-image: url('/static/img/expand-alt-white.svg');\n" +
                            '}'
                    )
                );
                style.append(
                    document.createTextNode(
                        '.inspectionImageDownloadButton {\n' +
                            "background-image: url('/static/img/download-white.svg');\n" +
                            '}'
                    )
                );
                style.append(
                    document.createTextNode(
                        '.hideAutoAnnotationsButton {\n' +
                            "background-image: url('/static/img/robot-white.svg');\n" +
                            '}'
                    )
                );
                style.append(
                    document.createTextNode(
                        '.hideAutoAnnotationsButton.hidden:after {\n' +
                            'background: white !important;\n' +
                            '}'
                    )
                );
            } else {
                styleDiv.innerHTML = '';
            }
        },
    },
    // eslint-disable-next-line sonarjs/cognitive-complexity
    mounted() {
        axios.defaults.xsrfCookieName = 'csrftoken';
        axios.defaults.xsrfHeaderName = 'X-CSRFTOKEN';

        var mql = window.matchMedia('(prefers-color-scheme: dark)');
        mql.addEventListener('change', (event) => {
            if (event.matches) this.setDarkTheme(true);
        });
        mql = window.matchMedia('(prefers-color-scheme: light)');
        mql.addEventListener('change', (event) => {
            if (event.matches) this.setDarkTheme(false);
        });

        let savedDarkTheme = null;
        try {
            savedDarkTheme = localStorage.getItem('darkTheme');
        } catch (error) {
            savedDarkTheme = null;
        }
        if (savedDarkTheme === 'true' || savedDarkTheme === 'false') {
            this.setDarkTheme(savedDarkTheme === 'true');
        } else {
            this.setDarkTheme(this.isOSDarkMode);
        }

        if (this.isTouchScreen) {
            delete Hammer.defaults.cssProps.userSelect;
            const hammertime = new Hammer(document.querySelector('#MainApp'));
            const $this = this;

            hammertime.on('swiperight', () => {
                $this.drawer = true;
            });

            hammertime.on('swipeleft', () => {
                $this.drawer = false;
            });
        }

        this.platformString = `${browser.getBrowserName()} ${browser.getBrowserVersion()} on ${browser.getOSName()} ${browser.getOSVersion()}`;
        this.platformType = browser.getPlatformType();

        if (!this.supportedBrowser) {
            if (
                window.location.pathname !== '/support/' &&
                window.location.pathname !== '/tools/compatibility/'
            ) {
                this.supportedBrowserDialog = true;
            } else {
                // eslint-disable-next-line no-alert
                window.alert(
                    'Your browser is not supported. Please upgrade to a supported browser.'
                );
            }
        }

        const sites = [{ name: 'All Locations', uuid: 'all_sites' }];
        this.sites = sites;
        this.sites_dict = this.siteDataDict;

        const url = new Url();
        if (url.query.reports) {
            this.reportsDialog = true;
        }

        if (url.query.sync) {
            this.synced_views = true;
        }

        if (!developmentServer) {
            window.Intercom('onShow', () => {
                this.intercomMessengerOpen = true;
            });

            window.Intercom('onHide', () => {
                this.intercomMessengerOpen = false;
            });
        }
    },
    beforeUnmount() {
        this._debouncedSearchDatasets?.cancel();
        if (this.sitesSearchTimeout) {
            clearTimeout(this.sitesSearchTimeout);
        }
    },
};

export default mainMixin;

window.ndmobilecheck = function ndmobilecheck() {
    browser.satisfies({
        mobile: {
            safari: '>=9',
            'android browser': '>3.10',
        },
    });
};
